package com.terapia.terasenior.ui.therapy

/**
 * Utilidad centralizada para traducir los nombres técnicos de los ejercicios
 * a lenguaje humano entendible por el usuario/terapeuta. (v1.3.42)
 */
object ExerciseTranslationUtils {
    fun getDisplayName(type: String): String {
        val cleanType = type.trim().lowercase()
        return when (cleanType) {
            // --- ORIENTACIÓN TEMPORAL ---
            "orientation_temporal" -> "Orientación Temporal"
            "orientation_temporal_day" -> "Día del mes"
            "orientation_temporal_month" -> "Mes del año"
            "orientation_temporal_year" -> "Año actual"
            "orientation_temporal_season" -> "Estación del año"
            "orientation_temporal_dayweek" -> "Día de la semana"
            "orientation_temporal_partday" -> "Parte del día"
            "orientation_temporal_hour" -> "Hora aproximada"
            "orientation_temporal_century" -> "Siglo actual"
            "orientation_temporal_decade" -> "Década actual"
            "orientation_temporal_semester" -> "Semestre/Trimestre"
            "orientation_temporal_yesterday" -> "Ayer qué día fue"
            "orientation_temporal_tomorrow" -> "Mañana qué día será"
            "orientation_temporal_lastweek" -> "Día hace una semana"
            "orientation_temporal_lastmonth" -> "Mes anterior"
            "orientation_temporal_nextmonth" -> "Mes próximo"
            "orientation_temporal_lastyear" -> "Año pasado"
            "orientation_temporal_nextyear" -> "Año próximo"
            "orientation_temporal_lastseason" -> "Estación anterior"
            "orientation_temporal_nextseason" -> "Estación próxima"
            "orientation_temporal_holiday" -> "Festividad cercana"
            "orientation_temporal_week_next" -> "Día próxima semana"
            "orientation_temporal_christmas" -> "Mes de Navidad"
            "orientation_temporal_newyear" -> "Día Año Nuevo"
            "orientation_temporal_night_light" -> "Luz nocturna"
            "orientation_temporal_day_light" -> "Luz diurna"
            "orientation_temporal_spring_month" -> "Mes de Primavera"
            "orientation_temporal_summer_month" -> "Mes de Verano"
            "orientation_temporal_autumn_month" -> "Mes de Otoño"
            "orientation_temporal_winter_month" -> "Mes de Invierno"

            // --- ORIENTACIÓN ESPACIAL ---
            "orientation_spatial_city" -> "Ciudad o Pueblo"
            "orientation_spatial_province" -> "Provincia"
            "orientation_spatial_region" -> "Comunidad Autónoma"
            "orientation_spatial_country" -> "País"
            "orientation_spatial_continent" -> "Continente"
            "orientation_spatial_building" -> "Edificio actual"
            "orientation_spatial_floor" -> "Planta del edificio"
            "orientation_spatial_room" -> "Habitación o Sala"
            "orientation_spatial_street" -> "Calle cercana"
            "orientation_spatial_monument" -> "Monumento cercano"
            "orientation_spatial_capital" -> "Capital de España"
            "orientation_spatial_hospital" -> "Nombre del centro"
            "orientation_spatial_place" -> "Lugar actual"
            "orientation_spatial_kitchen" -> "Uso de cocina"
            "orientation_spatial_bedroom" -> "Uso de dormitorio"
            "orientation_spatial_library" -> "Uso de biblioteca"
            "orientation_spatial_pharmacy" -> "Uso de farmacia"
            "orientation_spatial_bakery" -> "Uso de panadería"
            "orientation_spatial_ceiling" -> "Objeto en techo"
            "orientation_spatial_ocean" -> "Océano mayor"
            "orientation_spatial_moon_orbit" -> "Órbita lunar"
            "orientation_spatial_capital_spain" -> "Capital de España"
            "orientation_spatial_sunrise" -> "Salida del sol"
            "orientation_spatial_sunset" -> "Puesta del sol"
            "orientation_spatial_climate" -> "Clima regional"
            "orientation_spatial_sea" -> "Mar más cercano"
            "orientation_spatial_river" -> "Río de la zona"
            "orientation_spatial_north" -> "País al norte"
            "orientation_spatial_west" -> "País al oeste"
            "orientation_spatial_planet" -> "Planeta"

            // --- ORIENTACIÓN PERSONAL ---
            "orientation_personal_name" -> "Nombre propio"
            "orientation_personal_surname" -> "Primer apellido"
            "orientation_personal_birthdate" -> "Fecha de nacimiento"
            "orientation_personal_age" -> "Edad actual"
            "orientation_personal_job" -> "Antigua profesión"
            "orientation_personal_civil" -> "Estado civil"
            "orientation_personal_children" -> "Número de hijos"
            "orientation_personal_parents" -> "Nombre de padres"
            "orientation_personal_birthcity" -> "Ciudad natal"
            "orientation_personal_residence" -> "Residencia actual"
            "orientation_personal_food" -> "Comida favorita"
            "orientation_personal_color" -> "Color favorito"
            "orientation_personal_team" -> "Equipo favorito"
            "orientation_personal_hobby" -> "Afición o Hobby"
            "orientation_personal_meds" -> "Medicación"
            "orientation_personal_allergies" -> "Alergias"
            "orientation_personal_doctor" -> "Médico de cabecera"
            "orientation_personal_nurse" -> "Terapeuta/Enfermera"
            "orientation_personal_anniversary" -> "Aniversario"
            "orientation_personal_grandchildren" -> "Nombre de nietos"
            "orientation_personal_breakfast" -> "Desayuno de hoy"

            // --- ORIENTACIÓN SITUACIONAL ---
            "orientation_situational_why" -> "Motivo del ingreso"
            "orientation_situational_who" -> "Acompañante de hoy"
            "orientation_situational_feeling" -> "Estado de ánimo"
            "orientation_situational_king" -> "Rey de España"
            "orientation_situational_president" -> "Presidente Gobierno"
            "orientation_situational_currency" -> "Moneda"
            "orientation_situational_language" -> "Idioma"
            "orientation_situational_before" -> "Actividad anterior"
            "orientation_situational_after" -> "Actividad posterior"
            "orientation_situational_count" -> "Personas en la sala"
            "orientation_situational_walls" -> "Color de paredes"
            "orientation_situational_hand" -> "Objeto en mano"
            "orientation_situational_clothes" -> "Color de ropa"
            "orientation_situational_weather" -> "Tiempo exterior"
            "orientation_situational_snow" -> "Época de nieve"
            "orientation_situational_xmas" -> "Mes de Navidad"
            "orientation_situational_const" -> "Día Constitución"
            "orientation_situational_nextperson" -> "Persona al lado"
            "orientation_situational_partner" -> "Compañero de grupo"
            "orientation_situational_clock" -> "Uso del reloj"
            "orientation_situational_color_sky" -> "Color del cielo"
            "orientation_situational_color_grass" -> "Color de hierba"
            "orientation_situational_blood" -> "Color sangre"
            "orientation_situational_fire" -> "Sensación fuego"
            "orientation_situational_ice" -> "Sensación hielo"
            "orientation_situational_sun" -> "Salida del sol"
            "orientation_situational_lemon_taste" -> "Sabor limón"
            "orientation_situational_sugar_taste" -> "Sabor azúcar"
            "orientation_situational_sea_water" -> "Sabor agua mar"
            "orientation_situational_stop_color" -> "Color señal STOP"
            "orientation_situational_zebra_cross" -> "Color paso cebra"
            "orientation_situational_traffic_light_go" -> "Semáforo verde"
            "orientation_situational_traffic_light_stop" -> "Semáforo rojo"
            "orientation_situational_dog_sound" -> "Sonido perro"
            "orientation_situational_cat_sound" -> "Sonido gato"
            "orientation_situational_cow_sound" -> "Sonido vaca"
            "orientation_situational_sheep_sound" -> "Sonido oveja"
            "orientation_situational_milk_color" -> "Color leche"
            "orientation_situational_coal_color" -> "Color carbón"
            "orientation_situational_tomato_color" -> "Color tomate"
            "orientation_situational_banana_color" -> "Color plátano"
            "orientation_situational_dentist" -> "Médico de dientes"
            "orientation_situational_umbrella" -> "Uso paraguas"
            "orientation_situational_glasses" -> "Uso gafas"
            "orientation_situational_shoes_wear" -> "Uso zapatos"
            "orientation_situational_hat_wear" -> "Uso sombrero"
            "orientation_situational_gloves_wear" -> "Uso guantes"
            "orientation_situational_fridge_use" -> "Uso frigorífico"
            "orientation_situational_chair_use" -> "Uso silla"
            "orientation_situational_bed_use" -> "Uso cama"
            "orientation_situational_eyes_count" -> "Número de ojos"
            "orientation_situational_ears_count" -> "Número de orejas"
            "orientation_situational_nose_count" -> "Número de narices"
            "orientation_situational_mouth_count" -> "Número de bocas"
            "orientation_situational_head_count" -> "Número de cabezas"
            "orientation_situational_arms_count" -> "Número de brazos"
            "orientation_situational_legs_count" -> "Número de piernas"
            "orientation_situational_hair_color" -> "Color pelo anciano"
            "orientation_situational_sun_shape" -> "Forma del sol"
            "orientation_situational_ball_shape" -> "Forma de balón"
            "orientation_situational_table_use" -> "Uso de mesa"
            "orientation_situational_knife_use" -> "Uso de cuchillo"
            "orientation_situational_spoon_use" -> "Uso de cuchara"
            "orientation_situational_comb_use" -> "Uso de peine"
            "orientation_situational_soap_use" -> "Uso de jabón"
            "orientation_situational_towel_use" -> "Uso de toalla"
            "orientation_situational_broom_use" -> "Uso de escoba"
            "orientation_situational_oven_use" -> "Uso de horno"
            "orientation_situational_pill_use" -> "Uso de medicinas"
            "orientation_situational_phone_use" -> "Uso de teléfono"
            "orientation_situational_keys_use" -> "Uso de llaves"
            "orientation_situational_glasses_use" -> "Uso de gafas"
            "orientation_situational_watch_use" -> "Uso de reloj"
            "orientation_situational_wallet_use" -> "Uso de cartera"
            "orientation_situational_calendar_use" -> "Uso de calendario"
            "orientation_situational_doctor_tool" -> "Herramienta médico"
            "orientation_situational_firemen" -> "Función bomberos"
            "orientation_situational_stewardess" -> "Lugar azafata"
            "orientation_situational_pilot" -> "Función piloto"
            "orientation_situational_ship_captain" -> "Función capitán"
            "orientation_situational_cow_milk" -> "Origen leche"
            "orientation_situational_hen_eggs" -> "Origen huevos"
            "orientation_situational_bee_honey" -> "Origen miel"
            "orientation_situational_spider_web" -> "Origen tela araña"

            // --- CÁLCULO ---
            "orientation_calc_year_days" -> "Días del año"
            "orientation_calc_year_months" -> "Meses del año"
            "orientation_calc_week_days" -> "Días de la semana"
            "orientation_calc_day_hours" -> "Horas del día"
            "orientation_calc_hour_mins" -> "Minutos de la hora"
            "orientation_calc_month_10" -> "Mes número 10"
            "orientation_calc_day_3" -> "Tercer día semana"
            "orientation_calc_spring" -> "Inicio primavera"
            "orientation_calc_future_year" -> "Año en una década"
            "orientation_calc_birth_year" -> "Año de nacimiento"
            "orientation_calc_today_plus2" -> "Hoy más 2 días"
            "orientation_calc_last_3months" -> "Hace 3 meses"
            "orientation_calc_seasons_seq" -> "Las 4 estaciones"
            "orientation_calc_week_rev" -> "Semana al revés"
            "orientation_calc_months_rev" -> "Meses al revés"
            "orientation_calc_time_plus30" -> "Hora + 30 mins"
            "orientation_calc_king_city" -> "Ciudad del Rey"
            "orientation_calc_neighbor_city" -> "Ciudad vecina"
            "orientation_calc_sepad" -> "Nombre entidad"
            "orientation_calc_appname" -> "Nombre aplicación"
            "orientation_calc_feet_count" -> "Número de pies"
            "orientation_calc_hands_count" -> "Número de manos"
            "orientation_calc_fingers_hand" -> "Dedos en una mano"
            "orientation_calc_fingers_total" -> "Dedos totales"
            "orientation_calc_century_years" -> "Años en un siglo"
            "orientation_calc_decade_years" -> "Años en una década"
            "orientation_calc_dozen" -> "Unidades docena"
            "orientation_calc_half_dozen" -> "Unidades media docena"
            "orientation_calc_wheels_car" -> "Ruedas de coche"
            "orientation_calc_wheels_bike" -> "Ruedas de bicicleta"
            "orientation_calc_wheels_tricycle" -> "Ruedas de triciclo"
            
            // --- ATENCIÓN ---
            "number_search" -> "Busca el Número"
            "attention_different", "attention_spot_odd_one_out" -> "El Intruso"
            "attention_equals_model" -> "Buscar Iguales"
            "attention_positions" -> "Orientación Espacial"
            "attention_letters" -> "Búsqueda de Letras"
            "attention_numbers" -> "Búsqueda de Números"
            "attention_symbols" -> "Búsqueda de Símbolos"
            "attention_matrices" -> "Matrices de Atención"
            "attention_row_cancel" -> "Tachado por Filas"
            "attention_consecutive" -> "Números Consecutivos"
            "attention_yes_no" -> "Tachar Sí/No"
            "attention_dual_task" -> "Tarea Dual"
            "attention_count" -> "Contar Dibujos"
            "attention_longest" -> "Discriminación de Longitud"
            "attention_differences" -> "Encontrar Diferencias"
            "attention_missing_part" -> "Completar Dibujo"
            "attention_word_search" -> "Sopa de Letras"
            
            // --- MEMORIA ---
            "memory_pairs" -> "Parejas de Memoria"
            "memory_cultural" -> "Memoria Cultural"
            "memory_utility" -> "Utilidad de Objetos"
            "memory_needs" -> "Necesidades de Tarea"
            "memory_recent" -> "Memoria Reciente"
            "memory_cultural_inventor" -> "Inventor de la bombilla"
            "memory_cultural_discovery" -> "Descubrimiento de América"
            "memory_cultural_moon" -> "Llegada a la Luna"
            "memory_cultural_painting" -> "Pintor de Las Meninas"
            "memory_cultural_writer" -> "Autor de El Quijote"
            "memory_recent_today_weather" -> "Tiempo de hoy"
            "memory_recent_last_meal" -> "Cena de anoche"
            "memory_prospective_meds" -> "Hora de medicación"
            "memory_working_numbers_rev" -> "Números al revés"
            "memory_semantic_capital_italy" -> "Capital de Italia"
            "memory_semantic_capital_germany" -> "Capital de Alemania"
            "memory_semantic_capital_portugal" -> "Capital de Portugal"
            "memory_semantic_ocean_depth" -> "Animal mayor del océano"
            "memory_semantic_planet_red" -> "Planeta rojo"
            "memory_semantic_island_canary" -> "Isla de Canarias"
            "memory_semantic_island_balearic" -> "Isla de Baleares"
            "memory_semantic_river_egypt" -> "Río de Egipto"
            "memory_semantic_mountain_everest" -> "Montaña más alta"
            "memory_semantic_continent_kangaroo" -> "Continente de canguros"
            "memory_semantic_continent_pyramids" -> "Continente de pirámides"
            "memory_utility_hammer" -> "Uso del martillo"
            "memory_utility_scissors" -> "Uso de tijeras"
            "memory_utility_broom" -> "Uso de la escoba"
            "memory_utility_keys" -> "Uso de las llaves"
            "memory_utility_glasses" -> "Uso de las gafas"
            "memory_needs_coffee" -> "Necesidad para el café"
            "memory_needs_letter" -> "Necesidad para una carta"
            "memory_needs_wash_hair" -> "Necesidad para el pelo"
            "memory_needs_teeth" -> "Necesidad para los dientes"
            "memory_needs_rain" -> "Necesidad para la lluvia"
            "memory_semantic_fruit_yellow" -> "Fruta amarilla"
            "memory_semantic_fruit_red" -> "Fruta roja"
            "memory_semantic_animal_bark" -> "Animal que ladra"
            "memory_semantic_animal_meow" -> "Animal que maúlla"
            "memory_semantic_animal_moo" -> "Animal que muge"
            "memory_semantic_color_grass" -> "Color de la hierba"
            "memory_semantic_color_sky" -> "Color del cielo"
            "memory_semantic_color_coal" -> "Color del carbón"
            "memory_semantic_color_milk" -> "Color de la leche"
            "memory_semantic_color_blood" -> "Color de la sangre"
            "memory_semantic_season_cold" -> "Estación más fría"
            "memory_semantic_season_hot" -> "Estación más calurosa"
            "memory_semantic_day_first" -> "Primer día de la semana"
            "memory_semantic_day_last" -> "Último día de la semana"
            "memory_semantic_month_first" -> "Primer mes del año"
            "memory_semantic_clothing_feet" -> "Prenda para los pies"
            "memory_semantic_clothing_hands" -> "Prenda para las manos"
            "memory_semantic_clothing_head" -> "Prenda para la cabeza"
            "memory_semantic_tool_nails" -> "Herramienta para clavos"
            "memory_semantic_tool_screws" -> "Herramienta para tornillos"
            "memory_semantic_kitchen_fry" -> "Objeto para freír"
            "memory_semantic_kitchen_soup" -> "Objeto para la sopa"
            "memory_semantic_kitchen_bake" -> "Lugar para asar"
            "memory_semantic_home_sleep" -> "Mueble para dormir"
            "memory_semantic_home_sit" -> "Mueble para sentarse"
            "memory_semantic_home_clothes" -> "Lugar para la ropa"
            "memory_semantic_home_food" -> "Lugar para la comida"
            "memory_semantic_body_see" -> "Parte para ver"
            "memory_semantic_body_hear" -> "Parte para oír"
            "memory_semantic_body_smell" -> "Parte para oler"
            "memory_semantic_body_eat" -> "Parte para comer"
            "memory_semantic_body_walk" -> "Parte para caminar"
            "memory_semantic_body_write" -> "Parte para escribir"
            "memory_semantic_family_son" -> "Hijo del padre"
            "memory_semantic_family_father_father" -> "Padre del padre"
            "memory_semantic_family_sister" -> "Hija de la madre"
            "memory_semantic_family_uncle" -> "Hermano de la madre"
            "memory_semantic_family_nephew" -> "Hijo del hermano"
            "memory_semantic_object_umbrella" -> "Uso del paraguas"
            "memory_semantic_object_soap" -> "Uso del jabón"
            "memory_semantic_object_towel" -> "Uso de la toalla"
            "memory_semantic_object_keys" -> "Uso de las llaves"
            "memory_semantic_object_phone" -> "Uso del teléfono"
            "memory_semantic_object_tv" -> "Uso del televisor"
            "memory_semantic_object_radio" -> "Uso de la radio"
            "memory_semantic_object_clock" -> "Uso del reloj"
            "memory_semantic_object_money" -> "Uso del dinero"
            "memory_semantic_object_wallet" -> "Uso de la cartera"
            "memory_semantic_animal_hen" -> "Origen de los huevos"
            "memory_semantic_animal_cow" -> "Origen de la leche"
            "memory_semantic_animal_sheep" -> "Origen de la lana"
            "memory_semantic_animal_bee" -> "Origen de la miel"
            "memory_semantic_animal_spider" -> "Origen de la telaraña"
            "memory_semantic_food_bread" -> "Lugar compra pan"
            "memory_semantic_food_meat" -> "Lugar compra carne"
            "memory_semantic_food_fish" -> "Lugar compra pescado"
            "memory_semantic_food_fruit" -> "Lugar compra fruta"
            "memory_semantic_food_medicine" -> "Lugar compra medicina"
            "memory_semantic_place_church" -> "Lugar para la misa"
            "memory_semantic_place_hospital" -> "Lugar para enfermos"
            "memory_semantic_place_school" -> "Lugar para aprender"
            "memory_semantic_place_park" -> "Lugar para pasear"
            "memory_semantic_place_kitchen" -> "Lugar para cocinar"
            "memory_semantic_place_bathroom" -> "Lugar para la ducha"
            "memory_semantic_color_sun" -> "Color del sol"
            "memory_semantic_color_tomato" -> "Color del tomate"
            "memory_semantic_color_lemon" -> "Color del limón"
            "memory_semantic_color_orange" -> "Color de la naranja"
            "memory_cultural_first_king" -> "Primer Rey democracia"
            "memory_cultural_civil_war" -> "Siglo Guerra Civil"
            "memory_cultural_discovery_navigator" -> "Navegante de América"
            "memory_cultural_capital_france" -> "Capital de Francia"
            "memory_cultural_capital_uk" -> "Capital del Reino Unido"
            "memory_cultural_euro_intro" -> "Año entrada del Euro"
            "memory_cultural_olympics_barcelona" -> "Año Olimpiadas BCN"
            "memory_cultural_monalisa" -> "Pintor La Gioconda"
            "memory_cultural_beethoven" -> "Compositor 9ª Sinfonía"
            "memory_cultural_cervantes_birth" -> "Ciudad de Cervantes"
            
            // --- LENGUAJE ---
            "language_word_image" -> "Vocabulario: Imagen"
            "language_denomination", "language_naming_objects" -> "Denominación de Objetos"
            "language_semantic_category" -> "Categorías Semánticas"
            "language_start_letter" -> "Fluidez: Primera Letra"
            "language_start_syllable" -> "Fluidez: Primera Sílaba"
            "language_end_letter" -> "Fluidez: Letra Final"
            "language_end_syllable" -> "Fluidez: Sílaba Final"
            "language_complex_cluster" -> "Letras Trabadas"
            "language_semantic_completion" -> "Completar Categoría"
            "language_semantic_naming" -> "Nombrar Familia"
            
            // --- FUNCIONES EJECUTIVAS / CÁLCULO ---
            "calculation_simple" -> "Cálculo Mental"
            "executive_color_shape_sequence" -> "Secuencias Lógicas"
            "executive_planning_steps" -> "Planificación de Pasos"
            "executive_shopping_list" -> "Lista de la Compra"
            "executive_money_calculation" -> "Lógica Monetaria"
            "executive_time_logic" -> "Lógica Temporal"
            "executive_logical_reasoning" -> "Razonamiento Lógico"
            "executive_analogies" -> "Analogías Conceptuales"
            "executive_abstractions" -> "Abstracción Símbolo-Número"
            "executive_intrusos" -> "Tachar el Intruso"
            "executive_math_advanced" -> "Cálculo Avanzado"
            
            // --- PERCEPCIÓN ---
            "perception_color_identification" -> "Identificación de Colores"
            "perception_size_ordering" -> "Orden de Tamaños"
            "perception_lateral_dominance" -> "Derecha e Izquierda"
            "perception_mirror" -> "Imágenes en Espejo"
            "perception_body_parts" -> "Esquema Corporal"
            "perception_shape_fitting" -> "Encaje de Formas"
            
            // --- LECTOESCRITURA ---
            "literacy_tracing", "literacy_tracing_basic" -> "Trazos y Caligrafía"
            "literacy_complete_letters" -> "Completar Palabras"
            "literacy_copy_words" -> "Copia de Palabras"
            "literacy_form_shapes" -> "Dibujar Formas"
            
            else -> cleanType.replace("_", " ").replaceFirstChar { it.uppercase() }
        }
    }

    /**
     * Mapeo de niveles técnicos (1..5) a escala clínica GDS (v1.3.17).
     */
    fun getGdsLabel(level: Int): String = when (level) {
        1 -> "GDS 3"
        2 -> "GDS 3-4"
        3 -> "GDS 4"
        4 -> "GDS 4-5"
        5 -> "GDS 5"
        else -> "Nivel $level"
    }
}
